import React, { useState } from 'react';
import { FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { t, useLanguage } from '../useTranslations';
import '../styles/ConfirmModal.css';

/**
 * Reusable confirmation/alert modal — replaces browser-native window.confirm() and window.alert()
 *
 * Props:
 *   isOpen      {boolean}  – Whether the modal is visible
 *   message     {string}   – The message/question to display
 *   onConfirm   {func}     – Called when the confirm button is clicked (may be async)
 *   onCancel    {func}     – Called when the cancel button or overlay is clicked (optional)
 *   variant     {string}   – 'danger' | 'warning' | 'info' (default: 'danger')
 *   hideCancel  {boolean}  – Set true for alert-style dialogs (only confirm button shown)
 */
const ConfirmModal = ({
  isOpen,
  message,
  onConfirm,
  onCancel,
  variant = 'danger',
  hideCancel = false
}) => {
  useLanguage();
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!isLoading && !hideCancel && onCancel) onCancel();
  };

  const handleConfirm = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="confirm-modal-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true">
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`confirm-modal-icon confirm-icon-${variant}`}>
          {variant === 'info' ? <FaInfoCircle /> : <FaExclamationTriangle />}
        </div>
        <p className="confirm-modal-message">{message}</p>
        <div className="confirm-modal-actions">
          {!hideCancel && (
            <button className="confirm-btn-cancel" onClick={onCancel} disabled={isLoading}>
              {t('common.cancel')}
            </button>
          )}
          <button
            className={`confirm-btn-confirm confirm-btn-${variant}`}
            onClick={handleConfirm}
            disabled={isLoading}
            autoFocus
          >
            {isLoading ? (
              <>
                <span className="confirm-btn-spinner" aria-hidden="true" />
                {t('common.processing')}
              </>
            ) : (
              hideCancel ? t('common.close') : t('common.confirm')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
